/** biome-ignore-all lint/nursery/noSecrets: what biome thinks are secrets are just complex arbitrary tw classes */
"use client";

import * as React from "react";
import { forwardRef, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { getTextWidth } from "senf-shared/utils/helpers";
import { tv } from "tailwind-variants";
import { Button, Icon, Shake } from "../../..";
import { useFontStyles } from "../../../hooks/useFontStyles";
import { cn } from "../../../util/classNameConstructor";
import type { IconName } from "../icon/names";
import RichTextEditor from "../richTextEditor/RichTextEditor";
import TooltipProvider, {
  type TooltipProviderProps,
} from "../tooltip/TooltipProvider";
import CharacterCounter from "./CharacterCounter";

/**
 * Automatically resizes a textarea between minRows and maxRows.
 * Uses computed line-height and respects box-sizing.
 */
export function autoResizeTextarea(
  textarea: HTMLTextAreaElement,
  options: {
    minRows?: number;
    maxRows?: number;
  } = {},
): void {
  if (!textarea) return;

  const computedStyle = window.getComputedStyle(textarea);
  const borderBox = computedStyle.boxSizing === "border-box";
  const lineHeight = Number.parseFloat(computedStyle.lineHeight);

  if (!lineHeight || Number.isNaN(lineHeight)) return;

  const paddingOffset = borderBox
    ? Number.parseFloat(computedStyle.borderTopWidth || "0") +
      Number.parseFloat(computedStyle.borderBottomWidth || "0") +
      Number.parseFloat(computedStyle.paddingTop || "0") +
      Number.parseFloat(computedStyle.paddingBottom || "0")
    : 0;

  const minRows =
    options.minRows ??
    Number.parseInt(textarea.getAttribute("rows") || "1", 10);
  const maxRows = options.maxRows;

  const minHeight = Number(minRows) * lineHeight + paddingOffset;
  const maxHeight = maxRows
    ? Number(maxRows) * lineHeight + paddingOffset
    : Number.POSITIVE_INFINITY;

  textarea.style.height = "auto"; // Reset to shrink if needed
  const contentHeight = textarea.scrollHeight;

  const newHeight = Math.min(Math.max(contentHeight, minHeight), maxHeight);
  textarea.style.height = `${newHeight}px`;
  textarea.style.minHeight = `${0}px`;
}
const inputVariants = tv({
  slots: {
    container: ["group/input-container gap-y-1 flex flex-col w-full"],
    inputContainer: [
      "text-base",
      "[--input-px:0.75rem] [--input-ps:var(--input-px)] [--input-pe:var(--input-px)] [--input-py:0.75rem] [--input-pt:var(--input-py)] [--input-pb:var(--input-py)]",
      "ps-[var(--input-ps)] pe-[var(--input-pe)]",
      "relative cursor-text flex items-center rounded-xl w-full bg-surface-subdued duration-100 outline-none gap-2",
      "ring-inset ring-1 ring-border-subdued no-touch:hover:ring-border-default has-[:focus-visible]:ring-2 touch-any:has-[:focus-visible]:ring-focused-default",
      "disabled:opacity-50",
      // Autofill styles moved to container
      "has-[input:-webkit-autofill]:bg-[var(--palette-yellow-100)] has-[input:-webkit-autofill]:ring-[var(--palette-yellow-400)]",
      "no-touch:hover:has-[input:-webkit-autofill]:bg-[var(--palette-yellow-100)] no-touch:hover:has-[input:-webkit-autofill]:ring-[var(--palette-yellow-400)]",
      "has-[:focus-visible]:has-[input:-webkit-autofill]:bg-[var(--palette-yellow-100)] has-[:focus-visible]:has-[input:-webkit-autofill]:ring-[var(--palette-yellow-400)]",
      "[--border-width:1px] has-[:focus-visible]:[--border-width:2px]",
      "[&:has(:focus-visible)_.mdxeditor-toolbar]:!opacity-100 [&:has(:focus-visible)_[contenteditable]+div]:!text-text-placeholder",
    ],
    inputWrapper: [
      "relative flex min-w-0 w-full gap-1 items-baseline self-start pt-[var(--input-pt)] pb-[var(--input-pb)]",
    ],
    input: [
      "peer/input w-auto grow bg-transparent outline-none border-none p-0 min-w-0",
      "placeholder:text-text-subdued focus-visible:placeholder:text-text-placeholder",
      "disabled:opacity-50",
      // Hide browser default number input steppers
      "[&::-webkit-outer-spin-button]:appearance-none [&::-webkit-outer-spin-button]:m-0",
      "[&::-webkit-inner-spin-button]:appearance-none [&::-webkit-inner-spin-button]:m-0",
      "[&[type=number]]:[-moz-appearance:textfield]",
      // Autofill styles for text color only
      "autofill:[-webkit-text-fill-color:var(--text-default)]",
      "no-touch:hover:autofill:[-webkit-text-fill-color:var(--text-default)]",
      "focus-visible:autofill:[-webkit-text-fill-color:var(--text-default)]",
      "[--stepper-width:2rem] ",
    ],
    richTextEditor: [
      "[&_[contenteditable]+div]:!text-text-subdued",
      "[&_[contenteditable]]:!py-[var(--input-py)] [&_[contenteditable]+div]:py-[var(--input-py)]",
    ],
    label: [
      "text-text-subdued absolute start-[var(--input-ps)] transition-all duration-500 ease-expressive-out pointer-events-none",
      "cursor-text select-none origin-left w-max truncate",
      "max-w-[calc((100%-var(--leading-icon-offset,0px)-var(--trailing-icon-offset,0px)-var(--icon-gap,0px)-var(--input-ps,0px)-var(--input-pe,0px))/var(--scaled))]",
    ],
    leadingIcon: ["-mx-1 p-1 text-icon-default rounded-2xl shrink-0"],
    leadingIconWrapper: [""],
    trailingIcon: ["-mx-1 p-1 text-icon-default rounded-2xl shrink-0"],
    trailingIconWrapper: [""],
    stepper: [
      "group/stepper absolute flex flex-col justify-center border-l text-border-subdued border-current",
      "end-[var(--border-width)] top-[var(--border-width)] bottom-[var(--border-width)]",
    ],
    stepperButton: [
      "flex items-center justify-center w-8 p-0 pe-px me-[calc(2px-var(--border-width))] text-text-subdued duration-100 shrink-0",
      "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-focused-default",
      "disabled:opacity-50",
      "bg-transparent no-touch:hover:bg-transparent touch-any:active:bg-transparent",
      // if the current button is not disabled, text color should be default on hover
      "[&:not([disabled])]:no-touch:hover:!text-text-default",
      // if the stepper is hovered and there are no disabled buttons, the text colro should be placeholder
      "no-touch:group-hover/stepper:group-[&:not(:has([disabled]))]/stepper:text-text-placeholder",
    ],
    stepperDivider: ["border-b border-solid border-current"],
    helperText: ["text-xs text-text-subdued"],
  },
  variants: {
    isRich: {
      true: {
        richTextEditor: [
          "[&_[contenteditable]]:-m-3 [&_[contenteditable]]:!px-3 [&_[contenteditable]]:w-auto",
          "[&_.mdxeditor-toolbar]:-translate-y-full [&_.mdxeditor-toolbar]:pointer-events-all",
          "[&_.mdxeditor-toolbar]:-my-[0.5px]",
        ],
      },
      false: {},
    },
    size: {
      md: {
        inputContainer:
          "min-h-9 [--input-py:0.4375rem] [--stepper-width:1.5rem]",
        stepperButton: "h-4",
      },
      lg: {
        inputContainer: "min-h-11 [--input-py:0.6875rem]",
        stepperButton: "h-5",
      },
      xl: {
        inputContainer: "min-h-14 [--input-py:1.0625rem]",
        stepperButton: "h-6",
      },
    },
    hasLabel: {
      true: {
        label: "top-1",
      },
      false: {
        label: "top-1/2 -translate-y-1/2",
      },
    },
    labelOutside: {
      true: {
        label:
          "static text-sm !translate-y-0 !text-text-default pointer-events-auto cursor-default",
      },
      false: {
        label:
          "absolute start-[var(--input-ps)] transition-all duration-200 ease-expressive-out pointer-events-none cursor-text select-none origin-left",
      },
    },
    silent: {
      true: {
        inputContainer: [
          "bg-transparent ring-transparent w-auto",
          "ps-[var(--input-ps)] pe-[var(--input-pe)] -ms-[var(--input-ps)] -me-[var(--input-pe)]",
        ],
        inputWrapper: "h-full items-center",
        stepper: "border-none",
        stepperDivider: "border-none",
      },
      false: {},
    },
    hasError: {
      true: {
        inputContainer:
          "ring-border-critical-default has-[:focus-visible]:ring-border-critical-default",
        label: "text-text-critical",
        helperText: "text-text-critical",
      },
      false: {},
    },
    hasSuccess: {
      true: {
        inputContainer:
          "ring-border-success-default has-[:focus-visible]:ring-border-success-default",
        label: "text-text-success",
        helperText: "text-text-success",
      },
      false: {},
    },
    hasLeadingIcon: {
      true: {
        input: "",
        label: "start-10 [--leading-icon-offset:28px] [--icon-gap:1.5rem]",
      },
      false: {},
    },
    hasTrailingIcon: {
      true: {
        label: "end-10 [--trailing-icon-offset:28px] [--icon-gap:1.5rem]",
      },
      false: {},
    },
    hasStepper: {
      true: {
        // input: "pe-8 grow",
        inputContainer:
          "[--input-pe:calc(var(--input-px)+var(--stepper-width,0px))]",
        label: "end-8",
      },
      false: {},
    },
    isFloating: {
      true: {
        label: "scale-[var(--scaled)] text-text-subdued",
      },
      false: {
        label: [
          "top-1/2 -translate-y-1/2 text-text-subdued peer-autofill/input:top-1 peer-autofill/input:translate-y-0 peer-autofill/input:scale-[var(--scaled)] peer-autofill/input:text-text-subdued",
        ],
      },
    },
    isTextarea: {
      true: {
        inputContainer: "h-auto items-start",
        input:
          "resize-none block leading-snug -mt-[calc(var(--input-pt)-var(--border-width))] -mb-[calc(var(--input-pb)-var(--border-width))] pt-[calc(var(--input-pt)-var(--border-width))] pb-[calc(var(--input-pb)-var(--border-width))]",
      },
      false: {
        input: "leading-none",
      },
    },
    trailingIconClickable: {
      true: {
        trailingIcon:
          "cursor-pointer no-touch:hover:text-text-default focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focused-black",
      },
      false: {
        trailingIcon: "pointer-events-none",
      },
    },
  },
  compoundVariants: [
    // Size + hasLabel combinations for input padding-top (only when label is inside)
    {
      size: "md",
      hasLabel: true,
      labelOutside: false,
      class: {
        inputContainer: "[--input-pt:0.875rem] [--input-pb:0rem]",
      },
    },
    {
      size: "lg",
      hasLabel: true,
      labelOutside: false,
      class: {
        inputContainer: "[--input-pt:1.25rem] [--input-pb:0.125rem]",
      },
    },
    {
      size: "xl",
      hasLabel: true,
      labelOutside: false,
      class: {
        inputContainer: "[--input-pt:1.75rem] [--input-pb:0.375rem]",
      },
    },
    // Floating label positioning for different sizes (only when label is inside)
    {
      size: "md",
      isFloating: true,
      labelOutside: false,
      class: {
        label: "top-0.5",
      },
    },
    {
      size: "lg",
      isFloating: true,
      labelOutside: false,
      class: {
        label: "top-1",
      },
    },
    {
      size: "xl",
      isFloating: true,
      labelOutside: false,
      class: {
        label: "top-2",
      },
    },
    // Outside label positioning adjustments for leading icons
    {
      labelOutside: true,
      hasLeadingIcon: true,
      class: {
        label: "static text-text-default text-sm translate-y-0",
      },
    },
    // Textarea specific styles
    {
      isTextarea: true,
      hasLabel: true,
      isFloating: true,
      labelOutside: false,
      class: {
        label:
          "top-1 scale-[var(--scaled)] text-text-subdued bg-surface-subdued ring-[5px] ring-surface-subdued rounded",
      },
    },
    {
      isTextarea: true,
      hasLabel: true,
      isFloating: false,
      labelOutside: false,
      class: {
        label: "top-[1.375rem] text-text-subdued",
      },
    },
    // Textarea with outside label
    {
      isTextarea: true,
      labelOutside: true,
      class: {
        label: "static text-text-default text-sm translate-y-0",
      },
    },
    // Textarea with leading and trailing icons
    {
      isTextarea: true,
      size: "md",
      class: {
        leadingIconWrapper: "mt-1.5",
        trailingIconWrapper: "mt-1.5",
      },
    },
    {
      isTextarea: true,
      size: "lg",
      class: {
        leadingIconWrapper: "mt-2",
        trailingIconWrapper: "mt-2",
      },
    },
    {
      isTextarea: true,
      size: "xl",
      class: {
        leadingIconWrapper: "mt-3",
        trailingIconWrapper: "mt-3",
      },
    },
    // Rich text editor specific styles
    {
      isRich: true,
      size: "md",
      class: {
        inputContainer: "[--input-py:0.6225rem]",
        richTextEditor: ["[&_.mdxeditor-toolbar]:!top-0.5"],
      },
    },
    {
      isRich: true,
      size: "lg",
      class: {
        inputContainer: "[--input-py:0.8rem]",
        richTextEditor: ["[&_.mdxeditor-toolbar]:!top-0"],
      },
    },
    {
      isRich: true,
      size: "xl",
      class: {
        inputContainer: "[--input-py:1rem]",
        richTextEditor: [
          "[&_.mdxeditor-toolbar]:!top-1 [&_.mdxeditor-toolbar]:!rounded-md",
        ],
      },
    },
    {
      isRich: true,
      hasLabel: true,
      labelOutside: false,
      class: {
        richTextEditor: "[&_.mdxeditor-toolbar]:!rounded-md",
      },
    },
  ],
  defaultVariants: {
    size: "lg",
    hasError: false,
    hasSuccess: false,
    hasLeadingIcon: false,
    hasTrailingIcon: false,
    hasStepper: false,
    isTextarea: false,
    trailingIconClickable: false,
    labelOutside: false,
    singleRow: false,
    silent: false,
    isRich: false,
  },
});

// Generic interface that supports both string and number values
interface BaseInputProps {
  value: string | undefined;
  onChange?: (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => void;
  label?: string;
  helper?: string | boolean;
  error?: string | boolean;
  success?: string | boolean;
  leadingIcon?: IconName | React.ReactNode;
  leadingIconProps?: React.HTMLAttributes<HTMLDivElement> & {
    label?: TooltipProviderProps["primaryContent"];
  };
  trailingIcon?: IconName | React.ReactNode;
  trailingIconProps?: React.HTMLAttributes<HTMLDivElement> & {
    label?: TooltipProviderProps["primaryContent"];
  };
  size?: "md" | "lg" | "xl";
  labelOutside?: boolean;
  silent?: boolean;
  validate?: boolean | ((value: string | undefined) => Promise<boolean>);
  prepend?: React.ReactNode;
  append?: React.ReactNode;
  classNames?: {
    input?: string;
    label?: string;
    container?: string;
    inputContainer?: string;
    inputWrapper?: string;
    leadingIcon?: string;
    leadingIconWrapper?: string;
    trailingIcon?: string;
    trailingIconWrapper?: string;
  };
  rich?: never;
  rows?: never;
  maxRows?: never;
  characterCounter?: boolean;
}

export interface StringInputProps
  extends BaseInputProps,
    Omit<
      React.InputHTMLAttributes<HTMLInputElement>,
      "type" | "size" | "value" | "onChange"
    > {
  type: "email" | "password" | "search" | "tel" | "text" | "url" | undefined;
}

// Number input props
export interface NumberInputProps
  extends BaseInputProps,
    Omit<
      React.InputHTMLAttributes<HTMLInputElement>,
      "size" | "value" | "onChange"
    > {
  type: "number";
  stepper?: boolean;
  characterCounter?: never;
}

export interface TextareaProps
  extends Omit<BaseInputProps, "rich" | "rows" | "maxRows">,
    Omit<
      React.TextareaHTMLAttributes<HTMLTextAreaElement>,
      "type" | "size" | "value" | "onChange"
    > {
  type: "textarea";
  rich?: boolean;
  rows?: number;
  maxRows?: number | string;
}

export type InputProps = (
  | (Omit<StringInputProps, "type"> & { type?: StringInputProps["type"] })
  | NumberInputProps
  | TextareaProps
) & {
  onClick?: React.MouseEventHandler<HTMLElement>;
};

// Extract common input props to reduce redundancy
type CommonInputElementProps = Omit<InputProps, "onChange"> & {
  ref: React.Ref<HTMLInputElement | HTMLTextAreaElement>;
  onChange: (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => void;
  onFocus: (
    event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => void;
  onBlur: (
    event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => void;
  shouldFloat?: boolean;
  hasLabel?: boolean;
  hasOutsideLabel?: boolean;
};

// React component that renders the appropriate input element based on props
interface InputElementProps {
  isTextarea: boolean;
  rich: boolean;
  commonProps: CommonInputElementProps;
  textareaProps?: {
    rows: number;
    textareaSpecificProps: React.TextareaHTMLAttributes<HTMLTextAreaElement>;
  };
  richTextEditorProps?: {
    className?: string;
  };
  inputProps?: {
    effectiveInputType: string;
    inputMode?:
      | "search"
      | "email"
      | "tel"
      | "text"
      | "url"
      | "none"
      | "numeric"
      | "decimal";
    pattern?: string;
    style?: React.CSSProperties;
    inputSpecificProps: React.InputHTMLAttributes<HTMLInputElement>;
  };
  inputClasses: ReturnType<typeof inputVariants>["input"];
}

const useRtfHeight = (rows = 3) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const ref = useRef<HTMLDivElement | null>(null);
  const [contentEditablePadding, setContentEditablePadding] =
    useState<number>(0);

  useEffect(() => {
    if (!wrapperRef.current) return;
    ref.current = wrapperRef.current.querySelector("[contenteditable]");
    if (!ref.current) return;
    const { paddingTop, paddingBottom } = window.getComputedStyle(ref.current);
    setContentEditablePadding(
      Number.parseInt(paddingTop) + Number.parseInt(paddingBottom),
    );
  }, []);

  const [{ lineHeight }] = useFontStyles(
    ref as React.RefObject<HTMLDivElement>,
    [contentEditablePadding],
  );
  return [rows * lineHeight + contentEditablePadding, wrapperRef] as const;
};

// @todo: maybe implement addEventListener("programmaticFocus")

const InputElement = React.memo<InputElementProps>(
  ({
    isTextarea,
    rich,
    commonProps,
    textareaProps,
    richTextEditorProps,
    inputProps,
    inputClasses,
  }) => {
    const [rtfHeight, ref] = useRtfHeight(textareaProps?.rows);

    if (rich && isTextarea) {
      return (
        <div
          className="w-full"
          style={
            {
              "--content-min-height": `${rtfHeight}px`,
            } as React.CSSProperties
          }
          ref={ref}
        >
          <RichTextEditor
            value={commonProps.value ?? ""}
            className={cn(richTextEditorProps?.className)}
            contentEditableClassName={cn(
              commonProps.className,
              "min-h-[var(--content-min-height)]",
              "font-normal",
            )}
            placeholder={commonProps.placeholder}
            propSetIsFocused={(focused) => {
              const syntheticEvent = {} as React.FocusEvent<
                HTMLInputElement | HTMLTextAreaElement
              >;
              focused
                ? commonProps.onFocus(syntheticEvent)
                : commonProps.onBlur(syntheticEvent);
            }}
            onChange={(content) => {
              const syntheticEvent = {
                target: { value: content },
                currentTarget: { value: content },
              } as unknown as React.ChangeEvent<
                HTMLInputElement | HTMLTextAreaElement
              >;
              commonProps.onChange(syntheticEvent);
            }}
          />
        </div>
      );
    }

    // Textarea path
    if (isTextarea && textareaProps) {
      return (
        <textarea
          ref={commonProps.ref as React.Ref<HTMLTextAreaElement>}
          id={commonProps.id}
          value={commonProps.value}
          onChange={commonProps.onChange}
          onFocus={commonProps.onFocus}
          onBlur={commonProps.onBlur}
          disabled={commonProps.disabled}
          required={commonProps.required}
          placeholder={commonProps.placeholder}
          className={inputClasses({
            className: cn(
              "w-full",
              commonProps.className,
              commonProps.classNames?.input,
            ),
          })}
          aria-describedby={commonProps["aria-describedby"]}
          aria-invalid={commonProps["aria-invalid"]}
          aria-required={commonProps["aria-required"]}
          maxLength={commonProps.maxLength}
          minLength={commonProps.minLength}
          rows={textareaProps.rows}
          {...textareaProps.textareaSpecificProps}
        />
      );
    }

    // Input path
    if (inputProps) {
      return (
        <input
          ref={commonProps.ref as React.Ref<HTMLInputElement>}
          id={commonProps.id}
          type={inputProps.effectiveInputType}
          inputMode={inputProps.inputMode}
          pattern={inputProps.pattern}
          value={commonProps.value}
          onChange={commonProps.onChange}
          onFocus={commonProps.onFocus}
          onBlur={commonProps.onBlur}
          disabled={commonProps.disabled}
          required={commonProps.required}
          placeholder={commonProps.placeholder}
          className={inputClasses({
            className: cn(commonProps.className, commonProps.classNames?.input),
          })}
          aria-describedby={commonProps["aria-describedby"]}
          aria-invalid={commonProps["aria-invalid"]}
          aria-required={commonProps["aria-required"]}
          style={inputProps.style}
          {...inputProps.inputSpecificProps}
        />
      );
    }

    return null;
  },
);

export const Input = React.memo(
  forwardRef<
    Omit<HTMLInputElement | HTMLTextAreaElement, "size" | "onClick">,
    InputProps
  >(
    (
      {
        label,
        helper,
        error,
        success,
        leadingIcon,
        leadingIconProps,
        trailingIcon,
        trailingIconProps,
        className,
        classNames,
        value,
        onChange,
        onFocus,
        onBlur,
        onClick,
        disabled,
        required,
        silent,
        type = "text",
        id,
        placeholder,
        minLength,
        maxLength,
        size = "lg",
        labelOutside,
        validate,
        prepend,
        append,
        maxRows: _maxRows,
        ...props
      },
      ref,
    ) => {
      const { t } = useTranslation();
      const internalInputRef = React.useRef<
        HTMLInputElement | HTMLTextAreaElement
      >(null);
      const inputId = React.useId();
      const [isFocused, setIsFocused] = React.useState(false);
      const checkValue = React.useCallback((val: string | undefined) => {
        if (val == null) return false;
        if (typeof val === "string") return val.length > 0;
        return true;
      }, []);
      const [hasValue, setHasValue] = React.useState(checkValue(value));
      const [showPassword, setShowPassword] = React.useState(false);
      const [triggerErrorShake, setTriggerErrorShake] = React.useState(false);

      const isTextarea = type === "textarea";
      const isNumberType = type === "number";

      const rich = isTextarea && "rich" in props && props.rich;
      const min =
        isNumberType && "min" in props && props.min !== undefined
          ? Number(props.min)
          : undefined;
      const max =
        isNumberType && "max" in props && props.max !== undefined
          ? Number(props.max)
          : undefined;
      const step =
        isNumberType && "step" in props && props.step !== undefined
          ? Number(props.step)
          : 1;
      const rows =
        isTextarea && "rows" in props && props.rows !== undefined
          ? Number(props.rows)
          : 3;
      const maxRows =
        (isTextarea && (_maxRows !== undefined ? Number(_maxRows) : 10)) || 1;

      const stepper =
        isNumberType && (!("stepper" in props) || props.stepper !== false);

      const characterCounter =
        !isNumberType &&
        (!("characterCounter" in props) || props.characterCounter !== false);

      React.useEffect(() => {
        setHasValue(checkValue(value));
      }, [value, checkValue]);

      // Memoize the focus handler to prevent recreating on every render
      const handleSetFocus = React.useCallback(
        (
          e:
            | React.MouseEvent<HTMLDivElement>
            | React.KeyboardEvent<HTMLDivElement>,
        ) => {
          // Don't focus if clicking on interactive elements
          const target = e.target as HTMLElement;
          const isInteractive =
            target.matches("button") ||
            target.matches('[role="button"]') ||
            target.matches("input") ||
            target.matches("textarea");

          if (!isInteractive && internalInputRef.current) {
            e.preventDefault();
            internalInputRef.current.focus();
          }
        },
        [],
      );

      const handleFocus = React.useCallback(
        <T extends HTMLInputElement | HTMLTextAreaElement>(
          event: React.FocusEvent<T>,
        ) => {
          setIsFocused(true);
          (onFocus as React.FocusEventHandler<T>)?.(
            event as React.FocusEvent<T>,
          );
        },
        [onFocus],
      );

      const handleBlur = React.useCallback(
        <T extends HTMLInputElement | HTMLTextAreaElement>(
          event: React.FocusEvent<T>,
        ) => {
          setIsFocused(false);
          (onBlur as React.FocusEventHandler<T>)?.(
            event as React.FocusEvent<T>,
          );
          if (error) setTriggerErrorShake((e) => !e);
        },
        [onBlur, error],
      );

      const handleTextareaChange = React.useCallback(() => {
        if (
          isTextarea &&
          internalInputRef.current &&
          internalInputRef.current instanceof HTMLTextAreaElement
        ) {
          autoResizeTextarea(internalInputRef.current, {
            minRows: rows,
            maxRows,
          });
        }
      }, [rows, maxRows, isTextarea]);

      // biome-ignore lint/correctness/useExhaustiveDependencies: only run on mount
      React.useEffect(() => {
        handleTextareaChange();

        const resizeObserver = new ResizeObserver(handleTextareaChange);
        if (internalInputRef.current) {
          resizeObserver.observe(internalInputRef.current);
        }
        return () => {
          resizeObserver.disconnect();
        };
      }, [value]);

      const handleValidate = React.useCallback(
        async (val = value) => {
          const emailValidator = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

          // If validate is explicitly false, don't validate
          if (validate === false) {
            return null;
          }

          // If validate is a custom function, use it
          if (typeof validate === "function") {
            return await validate(val);
          }

          // Email validation
          if (type === "email") {
            if (!val || val.trim() === "") return null; // Don't validate empty emails
            return emailValidator.test(val);
          }

          // For other input types with validate=true, consider non-empty values as valid
          if (validate === true) {
            return Boolean(val && val.trim() !== "");
          }

          // Default: no validation
          return null;
        },
        [validate, type, value],
      );

      const [isValid, setIsValid] = React.useState<boolean | null>(null);

      // Run validation when value changes or component mounts
      React.useEffect(() => {
        if (validate !== undefined) {
          handleValidate().then(setIsValid);
        }
      }, [validate, handleValidate]);

      const handleChange = React.useCallback(
        (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
          if (validate !== undefined) {
            handleValidate(event.target.value).then(setIsValid);
          }
          setHasValue(checkValue(event.target.value));
          onChange?.(event);
        },
        [validate, handleValidate, onChange, checkValue],
      );

      // Memoize stepper handlers
      // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
      const handleStepUp = React.useCallback(() => {
        if (!isNumberType || disabled) return;

        const currentValue =
          typeof value === "number"
            ? value
            : Number.isNaN(Number(value))
              ? 0
              : Number(value);
        const stepValue =
          "step" in props && props.step ? Number(props.step) : 1;
        const maxValue =
          "max" in props && props.max !== undefined
            ? Number(props.max)
            : Number.MAX_SAFE_INTEGER;
        const newValue = Math.min(currentValue + stepValue, maxValue);

        // Create synthetic event for onChange
        if (onChange && internalInputRef.current) {
          const syntheticEvent = {
            target: { ...internalInputRef.current, value: newValue.toString() },
            currentTarget: {
              ...internalInputRef.current,
              value: newValue.toString(),
            },
          } as React.ChangeEvent<HTMLInputElement>;
          onChange(syntheticEvent);
        }
      }, [
        isNumberType,
        disabled,
        value,
        (props as unknown as NumberInputProps).step,
        (props as unknown as NumberInputProps).max,
        onChange,
      ]);

      // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
      const handleStepDown = React.useCallback(() => {
        if (!isNumberType || disabled) return;

        const currentValue =
          typeof value === "number"
            ? value
            : Number.isNaN(Number(value))
              ? 0
              : Number(value);
        const stepValue =
          "step" in props && props.step ? Number(props.step) : 1;
        const minValue =
          "min" in props && props.min !== undefined
            ? Number(props.min)
            : Number.MIN_SAFE_INTEGER;
        const newValue = Math.max(currentValue - stepValue, minValue);

        // Create synthetic event for onChange
        if (onChange && internalInputRef.current) {
          const syntheticEvent = {
            target: { ...internalInputRef.current, value: newValue.toString() },
            currentTarget: {
              ...internalInputRef.current,
              value: newValue.toString(),
            },
          } as React.ChangeEvent<HTMLInputElement>;
          onChange(syntheticEvent);
        }
      }, [
        isNumberType,
        disabled,
        value,
        (props as unknown as NumberInputProps).step,
        (props as unknown as NumberInputProps).min,
        onChange,
      ]);

      // Memoize computed values
      const shouldFloat = React.useMemo(
        () =>
          labelOutside
            ? false
            : Boolean(isFocused || hasValue || prepend || append),
        [labelOutside, isFocused, hasValue, prepend, append],
      );

      const hasError = React.useMemo(
        () =>
          Boolean(
            error ||
              (maxLength !== undefined && value && value.length > maxLength) ||
              (minLength !== undefined && value && value.length < minLength),
          ) || isValid === false,
        [error, maxLength, value, minLength, isValid],
      );

      React.useEffect(() => {
        if (error) setTriggerErrorShake((e) => !e);
      }, [error]);

      const hasSuccess = React.useMemo(() => {
        return (Boolean(success) || isValid === true) && !hasError;
      }, [success, isValid, hasError]);

      const descriptionText = React.useMemo(
        () =>
          (typeof error === "string" && error) ||
          (typeof success === "string" && success) ||
          (typeof helper === "string" && helper),
        [error, success, helper],
      );

      // Password toggle logic (not applicable for textarea or number)
      const isPasswordType =
        type === "password" && !isTextarea && !isNumberType;
      const isEmailType = type === "email" && !isTextarea && !isNumberType;
      const effectiveInputType = isPasswordType && showPassword ? "text" : type;

      const actualLeadingIcon = leadingIcon;
      const actualOnLeadingIconClick = leadingIconProps?.onClick;
      const { label: leadingTooltipLabel, ...restActualLeadingIconProps } =
        leadingIconProps || {};
      const actualLeadingTooltipLabel = leadingTooltipLabel;

      const actualLeadingIconProps = restActualLeadingIconProps;

      // Memoize trailing icon logic to prevent recalculation
      const trailingIconConfig = React.useMemo(() => {
        let actualTrailingIcon = trailingIcon;
        let actualOnTrailingIconClick = trailingIconProps?.onClick;
        const { label: trailingTooltipLabel, ...restActualTrailingIconProps } =
          trailingIconProps || {};
        let actualTrailingTooltipLabel = trailingTooltipLabel;

        const actualTrailingIconProps = restActualTrailingIconProps;

        if (isPasswordType) {
          actualTrailingIcon = showPassword ? "eye-strikethrough" : "eye";
          actualOnTrailingIconClick = () => setShowPassword(!showPassword);
          actualTrailingTooltipLabel = showPassword
            ? t("util:hide", { val: t("auth:password") })
            : t("util:show", { val: t("auth:password") });
        }

        if (isEmailType) {
          if (hasSuccess) actualTrailingIcon = "checkmark-circle-fill";
          actualTrailingIconProps.className = cn(
            "duration-500 ease-expressive-out",
            !trailingIcon && "opacity-0 scale-75",
            hasSuccess && "text-icon-success opacity-100 scale-100",
          );
        }

        return {
          actualTrailingIcon,
          actualOnTrailingIconClick,
          actualTrailingTooltipLabel,
          actualTrailingIconProps,
        };
      }, [
        trailingIcon,
        trailingIconProps,
        isPasswordType,
        showPassword,
        isEmailType,
        hasSuccess,
        t,
      ]);

      // Memoize inputVariants call
      const variantClasses = React.useMemo(
        () =>
          inputVariants({
            size,
            hasError,
            hasSuccess,
            hasLeadingIcon: Boolean(leadingIcon),
            hasTrailingIcon: Boolean(trailingIconConfig.actualTrailingIcon),
            hasStepper: stepper,
            isFloating: shouldFloat,
            hasLabel: !!label,
            isRich: Boolean(rich),
            isTextarea,
            trailingIconClickable: Boolean(
              trailingIconConfig.actualOnTrailingIconClick,
            ),
            labelOutside,
            silent,
          }),
        [
          size,
          hasError,
          hasSuccess,
          leadingIcon,
          trailingIconConfig.actualTrailingIcon,
          stepper,
          shouldFloat,
          label,
          rich,
          isTextarea,
          trailingIconConfig.actualOnTrailingIconClick,
          labelOutside,
          silent,
        ],
      );

      const {
        input: inputClasses,
        inputWrapper: inputWrapperClasses,
        label: labelClasses,
        leadingIcon: leadingIconClasses,
        leadingIconWrapper: leadingIconWrapperClasses,
        trailingIcon: trailingIconClasses,
        trailingIconWrapper: trailingIconWrapperClasses,
        stepper: stepperClasses,
        stepperButton: stepperButtonClasses,
        stepperDivider: stepperDividerClasses,
        container: containerClasses,
        inputContainer: inputContainerClasses,
        helperText: helperTextClasses,
        richTextEditor: richTextEditorClasses,
      } = variantClasses;

      // Combined ref handler that works with both callback refs and ref objects
      const combinedRef = React.useCallback(
        (node: HTMLInputElement | HTMLTextAreaElement | null) => {
          // Set our internal ref
          (
            internalInputRef as React.MutableRefObject<
              HTMLInputElement | HTMLTextAreaElement | null
            >
          ).current = node;

          // Handle the forwarded ref
          if (typeof ref === "function") {
            ref(node);
          } else if (ref) {
            ref.current = node;
          }
        },
        [ref],
      );

      const _placeholderWidth = useMemo(() => {
        const fontSize = size === "md" ? "12px" : "16px";
        return getTextWidth(placeholder || "", `${fontSize} Nunito`);
      }, [placeholder, size]);

      const renderIcon = React.useCallback(
        (position: "leading" | "trailing", wrapperClassName?: string) => {
          const onClick =
            position === "leading"
              ? actualOnLeadingIconClick
              : trailingIconConfig.actualOnTrailingIconClick;
          const tooltipLabel =
            position === "leading"
              ? actualLeadingTooltipLabel
              : trailingIconConfig.actualTrailingTooltipLabel;
          const iconProps =
            position === "leading"
              ? actualLeadingIconProps
              : trailingIconConfig.actualTrailingIconProps;
          const iconClasses =
            position === "leading" ? leadingIconClasses : trailingIconClasses;
          const iconName =
            position === "leading"
              ? actualLeadingIcon
              : trailingIconConfig.actualTrailingIcon;
          if (!iconName) return null;

          return (
            // biome-ignore lint/a11y/noStaticElementInteractions: mouse events are needed here
            // biome-ignore lint/a11y/useAriaPropsSupportedByRole: aria-label is needed here
            <div
              role={onClick ? "button" : undefined}
              tabIndex={onClick ? 0 : undefined}
              aria-label={
                typeof tooltipLabel === "string" ? tooltipLabel : undefined
              }
              onKeyDown={(e) => {
                if (onClick && (e.key === "Enter" || e.key === " ")) {
                  e.preventDefault();
                  onClick?.(e as unknown as React.MouseEvent<HTMLDivElement>);
                }
              }}
              onMouseDown={(e) => {
                e.preventDefault();
              }}
              onClick={onClick}
              {...iconProps}
              className={iconClasses({
                className: cn(
                  position === "leading" ? leadingIconProps : trailingIconProps,
                  position === "leading"
                    ? classNames?.leadingIcon
                    : classNames?.trailingIcon,
                  position === "leading"
                    ? actualLeadingIconProps.className
                    : trailingIconConfig.actualTrailingIconProps.className,
                  wrapperClassName,
                ),
              })}
            >
              <Icon
                name={iconName}
                className={cn("h-[1.125em] w-5 fill-current")}
                aria-hidden="true"
              />
            </div>
          );
        },
        [
          actualOnLeadingIconClick,
          trailingIconConfig,
          actualLeadingTooltipLabel,
          actualLeadingIconProps,
          leadingIconClasses,
          trailingIconClasses,
          actualLeadingIcon,
          leadingIconProps,
          trailingIconProps,
          classNames?.leadingIcon,
          classNames?.trailingIcon,
        ],
      );

      return (
        <div
          className={containerClasses({
            className: cn(classNames?.container),
          })}
          style={
            {
              "--scaled": shouldFloat ? (size === "xl" ? 0.875 : 0.75) : 1,
              ...props.style,
            } as React.CSSProperties
          }
        >
          {/* Outside Label */}
          {labelOutside && label && (
            <label
              htmlFor={id || inputId}
              className={labelClasses({ className: classNames?.label })}
              title={label}
            >
              {label}
              {/* {required && (
              <span aria-hidden="true" className="ms-1">
                *
              </span>
            )} */}
            </label>
          )}

          {/** biome-ignore lint/a11y/noStaticElementInteractions: mouse events are needed here */}
          <div
            className={inputContainerClasses({
              className: cn(classNames?.inputContainer),
            })}
            onClick={onClick}
            onMouseDown={handleSetFocus}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                handleSetFocus(e);
              }
            }}
          >
            {/* Leading Icon */}
            {leadingIcon && actualLeadingTooltipLabel ? (
              <TooltipProvider
                primaryContent={actualLeadingTooltipLabel}
                sideOffset={0}
                className={cn(
                  leadingIconWrapperClasses(),
                  classNames?.leadingIconWrapper,
                )}
              >
                {renderIcon("leading")}
              </TooltipProvider>
            ) : (
              renderIcon(
                "leading",
                cn(leadingIconWrapperClasses(), classNames?.leadingIconWrapper),
              )
            )}
            <div
              className={inputWrapperClasses({
                className: classNames?.inputWrapper,
              })}
            >
              {/* Prepend slot */}
              {prepend && (
                <div className={cn(className, "contents")}>{prepend}</div>
              )}

              {/* Input or Textarea */}
              <InputElement
                isTextarea={isTextarea}
                rich={Boolean(rich)}
                inputClasses={inputClasses}
                commonProps={{
                  ref: combinedRef,
                  id: id || inputId,
                  value,
                  size,
                  onChange: handleChange,
                  onFocus: handleFocus,
                  onBlur: handleBlur,
                  disabled,
                  required,
                  placeholder:
                    labelOutside || shouldFloat || !label
                      ? placeholder || ""
                      : "",
                  shouldFloat,
                  hasLabel: !!label,
                  hasOutsideLabel: labelOutside,
                  className,
                  classNames,
                  "aria-describedby": descriptionText
                    ? `${inputId}-description`
                    : undefined,
                  "aria-invalid": hasError,
                  "aria-required": required,
                  maxLength,
                  minLength,
                }}
                textareaProps={
                  isTextarea
                    ? {
                        rows,
                        textareaSpecificProps:
                          props as React.TextareaHTMLAttributes<HTMLTextAreaElement>,
                      }
                    : undefined
                }
                richTextEditorProps={
                  rich
                    ? {
                        className: richTextEditorClasses(),
                      }
                    : undefined
                }
                inputProps={
                  !isTextarea
                    ? {
                        effectiveInputType,
                        inputMode: isNumberType
                          ? "inputMode" in props && props.inputMode
                            ? props.inputMode
                            : "decimal"
                          : undefined,
                        pattern: isNumberType
                          ? "pattern" in props && props.pattern
                            ? props.pattern
                            : step % 1 === 0
                              ? "[0-9]*"
                              : undefined
                          : undefined,
                        style: {
                          width: !placeholder ? "0px" : undefined,
                        },
                        inputSpecificProps:
                          props as React.InputHTMLAttributes<HTMLInputElement>,
                      }
                    : undefined
                }
              />
              {/* Append slot */}
              {append && (
                <div className={cn(className, "contents")}>{append}</div>
              )}
            </div>
            {/* Floating Label - only when label is inside */}
            {!labelOutside && label && (
              <label
                htmlFor={id || inputId}
                onClick={() => internalInputRef.current?.focus()}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    internalInputRef.current?.focus();
                  }
                }}
                className={labelClasses({
                  className: cn(!shouldFloat && className, classNames?.label),
                })}
                title={label}
              >
                <span>
                  {label}
                  {/* {required && (
                  <span aria-hidden="true" className="ms-1">
                    *
                  </span>
                )} */}
                </span>
              </label>
            )}
            {isNumberType && stepper && (
              <div className={stepperClasses()}>
                <Button
                  type="button"
                  variant="shapeless"
                  className={stepperButtonClasses()}
                  onClick={handleStepUp}
                  disabled={
                    disabled ?? (max !== undefined && Number(value) >= max)
                  }
                  disabledNotice={t("stepper.maxReached", {
                    max,
                  })}
                  aria-label={t("stepper.increase")}
                  tabIndex={-1}
                  leadingIcon="arrowhead-up-md"
                  leadingIconProps={{
                    className: "h-5 w-5",
                    "aria-hidden": true,
                  }}
                />
                <div className={stepperDividerClasses()} />
                <Button
                  type="button"
                  variant="shapeless"
                  className={stepperButtonClasses()}
                  onClick={handleStepDown}
                  disabled={
                    disabled || (min !== undefined && Number(value) <= min)
                  }
                  disabledNotice={t("stepper.minReached", {
                    min,
                  })}
                  aria-label={t("stepper.decrease")}
                  tabIndex={-1}
                  leadingIcon="arrowhead-down-md"
                  leadingIconProps={{
                    className: "h-5 w-5",
                    "aria-hidden": true,
                  }}
                />
              </div>
            )}
            {/* Trailing Icon - only for input, not textarea or number */}
            {trailingIconConfig.actualTrailingIcon &&
            trailingIconConfig.actualTrailingTooltipLabel ? (
              <TooltipProvider
                primaryContent={trailingIconConfig.actualTrailingTooltipLabel}
                sideOffset={0}
                className={cn(
                  trailingIconWrapperClasses(),
                  classNames?.trailingIconWrapper,
                )}
              >
                {renderIcon("trailing")}
              </TooltipProvider>
            ) : (
              renderIcon(
                "trailing",
                cn(
                  trailingIconWrapperClasses(),
                  classNames?.trailingIconWrapper,
                ),
              )
            )}
          </div>

          {characterCounter &&
            (descriptionText ||
              maxLength !== undefined ||
              minLength !== undefined) && (
              <div className={"flex justify-between"}>
                <Shake trigger={triggerErrorShake}>
                  {descriptionText && (
                    <p
                      id={`${inputId}-description`}
                      className={helperTextClasses()}
                    >
                      {descriptionText}
                    </p>
                  )}
                </Shake>
                <CharacterCounter
                  value={value ?? ""}
                  minLength={minLength}
                  maxLength={maxLength}
                  inputRef={internalInputRef}
                />
              </div>
            )}
        </div>
      );
    },
  ),
);

Input.displayName = "Input";
